import { BrowserRouter, Navigate, Route, Routes } from 'react-router-dom';
import { SessionProvider } from '@client/lib/session';
import { RequireAuth } from '@client/components/RequireAuth';
import { AppLayout } from '@client/components/AppLayout';
import { LoginPage } from '@client/pages/LoginPage';
import { RegisterPage } from '@client/pages/RegisterPage';
import { AcceptInvitePage } from '@client/pages/AcceptInvitePage';
import { TodayPage } from '@client/pages/TodayPage';
import { CalendarPage } from '@client/pages/CalendarPage';
import { JobDetailPage } from '@client/pages/JobDetailPage';
import { NewJobPage } from '@client/pages/NewJobPage';
import { ServicesPage } from '@client/pages/ServicesPage';
import { RecurringPage } from '@client/pages/RecurringPage';
import { NewQuotePage } from '@client/pages/NewQuotePage';
import { NewInvoicePage } from '@client/pages/NewInvoicePage';
import { RecurringDetailPage } from '@client/pages/RecurringDetailPage';
import { AnalyticsPage } from '@client/pages/AnalyticsPage';
import { SettingsPage } from '@client/pages/SettingsPage';
import { QuotesPage } from '@client/pages/QuotesPage';
import { QuoteDetailPage } from '@client/pages/QuoteDetailPage';
import { CustomersPage } from '@client/pages/CustomersPage';
import { CustomerDetailPage } from '@client/pages/CustomerDetailPage';
import { InvoicesPage } from '@client/pages/InvoicesPage';
import { InvoiceDetailPage } from '@client/pages/InvoiceDetailPage';
import { PublicQuotePage } from '@client/pages/PublicQuotePage';
import { PublicInvoicePage } from '@client/pages/PublicInvoicePage';

function Placeholder({ title }: { title: string }) {
  return (
    <main className="placeholder">
      <h1>{title}</h1>
      <p>Ball &amp; Chaney Honey Do</p>
    </main>
  );
}

function Jobs() {
  return <Placeholder title="Jobs" />;
}

/**
 * Route shell: SessionProvider at the root, the three sign-in routes public,
 * everything else inside RequireAuth + AppLayout.
 *
 * The public set is /login, /register and /accept-invite/:token. All three MUST
 * sit outside RequireAuth — a route for someone who does not yet have an
 * account cannot be behind a check for having one, which would bounce every
 * invited member straight back to a login they cannot pass.
 */
export function App() {
  return (
    <SessionProvider>
      <BrowserRouter>
        <Routes>
          <Route path="/login" element={<LoginPage />} />
          <Route path="/register" element={<RegisterPage />} />
          <Route path="/accept-invite/:token" element={<AcceptInvitePage />} />
          {/* Public share links — no auth required. */}
          <Route path="/q/:token" element={<PublicQuotePage />} />
          <Route path="/i/:token" element={<PublicInvoicePage />} />
          <Route
            element={
              <RequireAuth>
                <AppLayout />
              </RequireAuth>
            }
          >
            <Route path="/" element={<Navigate to="/today" replace />} />
            <Route path="/today" element={<TodayPage />} />
            <Route path="/dashboard" element={<AnalyticsPage />} />
            <Route path="/calendar" element={<CalendarPage />} />
            <Route path="/customers" element={<CustomersPage />} />
            <Route path="/customers/:id" element={<CustomerDetailPage />} />
            <Route path="/jobs" element={<Jobs />} />
            <Route path="/jobs/new" element={<NewJobPage />} />
            <Route path="/jobs/:id" element={<JobDetailPage />} />
            <Route path="/quotes" element={<QuotesPage />} />
            {/* Before /quotes/:id, or "new" is matched as an id. */}
            <Route path="/quotes/new" element={<NewQuotePage />} />
            <Route path="/quotes/:id" element={<QuoteDetailPage />} />
            <Route path="/invoices" element={<InvoicesPage />} />
            {/* Before /invoices/:id, or "new" is matched as an id. */}
            <Route path="/invoices/new" element={<NewInvoicePage />} />
            <Route path="/invoices/:id" element={<InvoiceDetailPage />} />
            <Route path="/services" element={<ServicesPage />} />
            <Route path="/recurring" element={<RecurringPage />} />
            <Route path="/recurring/:id" element={<RecurringDetailPage />} />
            <Route path="/settings" element={<SettingsPage />} />
            <Route path="*" element={<Placeholder title="Not found" />} />
          </Route>
        </Routes>
      </BrowserRouter>
    </SessionProvider>
  );
}
